/**
 * Learning Section Component
 * Dashboard section for learning tools like GED Pathway
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, BookOpen, ArrowRight, Clock, Target,
  CheckCircle, Lock, Trophy, Sparkles, ChevronRight,
  Calculator, FlaskConical, Globe2, FileText
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUBJECT_ICONS = {
  Math: Calculator,
  RLA: BookOpen,
  Science: FlaskConical,
  SocialStudies: Globe2
};

const SUBJECT_COLORS = {
  Math: 'bg-blue-500',
  RLA: 'bg-purple-500',
  Science: 'bg-green-500',
  SocialStudies: 'bg-orange-500'
};

export default function LearningSection() {
  const navigate = useNavigate();
  const [gedData, setGedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [userNeedsGED, setUserNeedsGED] = useState(false);

  useEffect(() => {
    fetchGEDStatus();
  }, []);

  const fetchGEDStatus = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setLoading(false);
        return;
      }

      const headers = { 'Authorization': `Bearer ${token}` };

      // First check if user needs GED based on GPS responses or profile
      try {
        const gpsRes = await fetch(`${API_URL}/api/gps/responses`, { headers });
        if (gpsRes.ok) {
          const gpsData = await gpsRes.json();
          // Check if user indicated they need GED/didn't graduate HS
          const educationResponse = gpsData?.responses?.education_level;
          const needsGED = educationResponse?.value === 'no_hs_diploma' || 
                          educationResponse?.value === 'ged_in_progress' ||
                          educationResponse?.value === 'some_high_school';
          setUserNeedsGED(needsGED);
          
          if (!needsGED) {
            setLoading(false);
            return; // Don't fetch GED data if user doesn't need it
          }
        }
      } catch (e) {
        // If GPS check fails, check user profile
        try {
          const userRes = await fetch(`${API_URL}/api/users/me`, { headers });
          if (userRes.ok) {
            const userData = await userRes.json();
            const needsGED = userData.needs_ged === true || 
                            userData.education_level === 'no_hs_diploma' ||
                            userData.education_level === 'some_high_school' ||
                            userData.ged_enrolled === true;
            setUserNeedsGED(needsGED);
            
            if (!needsGED) {
              setLoading(false);
              return;
            }
          }
        } catch (e) {
          console.error('Error checking user profile:', e);
        }
      }

      // Fetch GED application status (only if user needs GED)
      const appRes = await fetch(`${API_URL}/api/ged/application`, { headers });
      let application = null;
      if (appRes.ok) {
        application = await appRes.json();
        // If user has a GED application, they need GED
        if (application) {
          setUserNeedsGED(true);
        }
      }

      // Fetch GED journey if approved
      let journey = null;
      if (application?.status === 'approved') {
        const journeyRes = await fetch(`${API_URL}/api/ged/journey`, { headers });
        if (journeyRes.ok) {
          journey = await journeyRes.json();
        }
      }

      setGedData({ application, journey });

      // GED Getting Started modal is now shown ONLY when user visits the GED Dashboard
      // Not on the main dashboard - to prevent overlap with the main Welcome Tour
      // See PathwayGEDDashboard.js for the tour trigger
    } catch (error) {
      console.error('Error fetching GED status:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallProgress = () => {
    if (!gedData?.journey?.subjects) return 0;
    const subjects = gedData.journey.subjects;
    const totalProgress = subjects.reduce((sum, s) => sum + (s.progress_percent || 0), 0);
    return Math.round(totalProgress / subjects.length);
  };

  const getNextSubject = () => {
    if (!gedData?.journey?.subjects) return null;
    return gedData.journey.subjects.find(s => s.status !== 'Completed');
  };

  // Hide section entirely if user doesn't need GED and hasn't enrolled
  if (loading) {
    return null; // Don't show loading state, just hide
  }

  if (!userNeedsGED && !gedData?.application) {
    return null; // User doesn't need GED - hide section
  }

  // Not enrolled but user needs GED - Show info card
  if (!gedData?.application) {
    return (
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Learning
          </h2>
        </div>

        <Card className="overflow-hidden border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-8 h-8 text-green-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">Pathway GED™ Program</h3>
                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Free</Badge>
                </div>
                <p className="text-gray-600 mb-4">
                  Prepare for your GED exam with our comprehensive learning program. Interactive lessons, 
                  practice tests, AI-powered tutoring, and study groups to help you succeed.
                </p>
                <div className="flex flex-wrap gap-3 mb-4">
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>4 GED Subjects</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Practice Tests</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>AI Tutor</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Study Groups</span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/pathway-ged')}
                  className="bg-green-600 hover:bg-green-700"
                >
                  Learn More & Apply
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  // Application pending
  if (gedData.application.status === 'submitted' || gedData.application.status === 'more_info_requested') {
    return (
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Learning
          </h2>
        </div>

        <Card className="overflow-hidden border-yellow-200 bg-gradient-to-br from-yellow-50 to-amber-50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-bold text-gray-900">Pathway GED™ Application</h3>
                  <Badge className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100">
                    {gedData.application.status === 'more_info_requested' ? 'Info Requested' : 'Under Review'}
                  </Badge>
                </div>
                <p className="text-gray-600 mb-4">
                  {gedData.application.status === 'more_info_requested' 
                    ? 'We need some additional information to process your application. Please click below to provide the requested details.'
                    : 'Your application is being reviewed by our team. You\'ll receive an email notification once it\'s approved. This typically takes 1-2 business days.'}
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <span>Submitted: {new Date(gedData.application.submitted_at).toLocaleDateString()}</span>
                </div>
                <Button 
                  variant={gedData.application.status === 'more_info_requested' ? 'default' : 'outline'}
                  onClick={() => navigate('/pathway-ged')}
                >
                  {gedData.application.status === 'more_info_requested' ? 'Provide Information' : 'View Application Status'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    );
  }

  // Approved - Show progress card
  const overallProgress = calculateOverallProgress();
  const nextSubject = getNextSubject();

  return (
    <>
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-green-600" />
            Learning
          </h2>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/pathway-ged')}
            className="text-green-600 hover:text-green-700"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {/* GED Progress Card */}
        <Card className="overflow-hidden border-green-200 bg-gradient-to-br from-green-50 to-emerald-50 mb-4">
          <CardContent className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Progress Summary */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Pathway GED™</h3>
                    <p className="text-sm text-gray-500">Your GED Learning Journey</p>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Overall Progress</span>
                    <span className="font-semibold text-green-600">{overallProgress}%</span>
                  </div>
                  <Progress value={overallProgress} className="h-2" />
                </div>

                {/* Subject Progress Grid */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {gedData.journey?.subjects?.map(subject => {
                    const Icon = SUBJECT_ICONS[subject.subject] || BookOpen;
                    return (
                      <div 
                        key={subject.subject}
                        className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-100"
                      >
                        <div className={`w-8 h-8 ${SUBJECT_COLORS[subject.subject]} rounded-lg flex items-center justify-center`}>
                          <Icon className="w-4 h-4 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{subject.subject}</p>
                          <p className="text-xs text-gray-500">{subject.progress_percent || 0}%</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {nextSubject && (
                  <Button 
                    onClick={() => navigate(`/pathway-ged/subject/${nextSubject.subject}`)}
                    className="w-full bg-green-600 hover:bg-green-700"
                  >
                    Continue: {nextSubject.subject}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Quick Links */}
              <div className="lg:w-48 flex flex-row lg:flex-col gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="flex-1 lg:w-full justify-start"
                  onClick={() => navigate('/pathway-ged/ai-help')}
                >
                  <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                  AI Tutor
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 lg:w-full justify-start"
                  onClick={() => navigate('/pathway-ged/timed-test')}
                >
                  <Target className="w-4 h-4 mr-2 text-blue-500" />
                  Practice Test
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex-1 lg:w-full justify-start"
                  onClick={() => navigate('/pathway-ged/resources')}
                >
                  <FileText className="w-4 h-4 mr-2 text-orange-500" />
                  Resources
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Getting Started Modal */}
      <GettingStartedModal 
        open={showGettingStarted}
        onClose={() => setShowGettingStarted(false)}
      />
    </>
  );
}

// Getting Started Modal Component
function GettingStartedModal({ open, onClose }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  const steps = [
    {
      icon: Trophy,
      title: "Congratulations! You're Approved!",
      description: "Welcome to the Pathway GED™ program. You now have full access to all learning materials, practice tests, and support resources.",
      color: "text-green-600",
      bgColor: "bg-green-100"
    },
    {
      icon: Target,
      title: "How CareerSupport.io + GED.com Work Together",
      description: "CareerSupport.io is your FREE learning and preparation platform. When you're ready to take the official GED test, you'll register and schedule through GED.com (separate paid service). We'll guide you through every step!",
      color: "text-blue-600",
      bgColor: "bg-blue-100"
    },
    {
      icon: BookOpen,
      title: "Your Learning Journey",
      description: "You'll study 4 subjects: Math, Language Arts, Science, and Social Studies. Complete lessons, take practice tests, and aim for a score of 145+ on each subject (the official passing score).",
      color: "text-purple-600",
      bgColor: "bg-purple-100"
    },
    {
      icon: Sparkles,
      title: "Get Help Anytime",
      description: "Stuck on a concept? Use our AI Tutor 24/7 for instant help. Join study groups to connect with other GED students. Track your progress and celebrate achievements along the way!",
      color: "text-orange-600",
      bgColor: "bg-orange-100"
    }
  ];

  const currentStep = steps[step];
  const Icon = currentStep.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className={`w-16 h-16 ${currentStep.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Icon className={`w-8 h-8 ${currentStep.color}`} />
          </div>
          <DialogTitle className="text-center text-xl">{currentStep.title}</DialogTitle>
          <DialogDescription className="text-center pt-2">
            {currentStep.description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center gap-2 my-4">
          {steps.map((_, i) => (
            <div 
              key={i}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === step ? 'bg-green-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        <div className="flex gap-3">
          {step > 0 && (
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setStep(step - 1)}
            >
              Back
            </Button>
          )}
          {step < steps.length - 1 ? (
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => setStep(step + 1)}
            >
              Next
            </Button>
          ) : (
            <Button 
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={() => {
                onClose();
                navigate('/pathway-ged');
              }}
            >
              Start Learning
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
